// UK GDPR Article 13: Information to be provided where personal data are collected from the data subject
// DPA 2018 compliance: Transparency about criminal conviction data processing

export default function PrivacyPolicy() {
  return (
    <div className="page-container">
      <div className="content-wrapper">
        <div className="page-header">
          <h1 className="page-title">Privacy Policy</h1>
        </div>
        
        <div className="prose space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
            <p>
              Second Chance Connect ("we", "us", "our") is committed to protecting the privacy of visitors and users of our website and services. This privacy policy explains what personal data we collect, why we collect it, how we use it, how we protect it, your rights under UK data protection law, and how to contact us about privacy matters. This policy is written to meet the requirements of the UK General Data Protection Regulation and the Data Protection Act 2018.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Data Controller</h2>
            <p>
              Second Chance Connect is the data controller responsible for personal data collected through this website and our services.
            </p>
            <p className="font-semibold mt-2">Controller contact:</p>
            <ul className="list-disc ml-6">
              <li>Organisation: Second Chance Connect Ltd</li>
              <li>Email: admin@sccapp.co.uk</li>
              <li>Phone: +44 07718942231</li>
              <li>Address: [To be provided]</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Personal Data We Collect</h2>
            <p>
              We collect personal data you provide directly and data collected automatically when you use the website.
            </p>
            
            <h3 className="text-xl font-semibold mt-4 mb-2">Provided Directly</h3>
            <ul className="list-disc ml-6">
              <li>Name, email address, phone number, postal address</li>
              <li>User account details and authentication credentials</li>
              <li>Payment tokens or references</li>
              <li>Profile information and employment history</li>
              <li>User communications and uploaded files</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4 mb-2">Sensitive or Special-Category Data</h3>
            <p className="font-semibold text-destructive">
              Criminal Conviction Information (DPA 2018 Schedule 1)
            </p>
            <p>
              Only collected where explicitly provided and necessary for the service (for job seekers to support rehabilitation and fair hiring practices). We process this data only with explicit consent and additional safeguards, implementing progressive disclosure to reduce discrimination while complying with the Rehabilitation of Offenders Act 1974.
            </p>

            <h3 className="text-xl font-semibold mt-4 mb-2">Automatically Collected</h3>
            <ul className="list-disc ml-6">
              <li>IP address and device/browser information</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Analytics and usage data</li>
              <li>Timestamps and log data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Purposes and Lawful Bases for Processing</h2>
            <p>We process personal data for limited purposes and always record the lawful basis for each purpose.</p>
            
            <div className="overflow-x-auto mt-4">
              <table className="min-w-full border border-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="border border-border px-4 py-2 text-left">Purpose</th>
                    <th className="border border-border px-4 py-2 text-left">Lawful Basis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border px-4 py-2">Provide the service and perform contracts with users</td>
                    <td className="border border-border px-4 py-2">Performance of a contract (GDPR Article 6(1)(b))</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Register and manage user accounts, billing, and support</td>
                    <td className="border border-border px-4 py-2">Performance of a contract; Legitimate interests for fraud prevention</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Process criminal conviction data for job matching</td>
                    <td className="border border-border px-4 py-2">Explicit Consent (GDPR Article 6(1)(a)) + DPA 2018 Schedule 1, Part 2</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Provide personalised features and improve the service</td>
                    <td className="border border-border px-4 py-2">Consent or Legitimate interests (GDPR Article 6(1)(f))</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Comply with legal obligations and prevent fraud</td>
                    <td className="border border-border px-4 py-2">Legal obligation (GDPR Article 6(1)(c)) and Legitimate interests</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Marketing communications (where used)</td>
                    <td className="border border-border px-4 py-2">Consent (GDPR Article 6(1)(a))</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to provide core functionality, remember user preferences, and collect analytics. We classify cookies into: strictly necessary, performance, functionality, and marketing.
            </p>
            <p className="mt-2">
              On first visit users are offered clear choices to accept or decline non-essential cookies. Cookie settings can be changed at any time via the cookie preferences control on the site. See our{" "}
              <a href="/cookies" className="text-primary underline">Cookie Policy</a> for full details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Data Minimisation and Pseudonymisation</h2>
            <p>
              We only collect data necessary for specified purposes. Where appropriate we pseudonymise or hash identifiers and store raw sensitive values in an encrypted vault with restricted access and audit logging. We avoid storing special-category data unless explicitly required and justified.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Sharing Data with Third Parties</h2>
            <p>
              We share personal data only with trusted processors necessary to deliver the service. Before sharing we carry out due diligence, enter Data Processing Agreements, restrict what data is shared, and where possible use privacy-preserving configurations.
            </p>
            <ul className="list-disc ml-6 mt-2">
              <li><strong>Potential Employers:</strong> Only with your explicit consent using progressive disclosure</li>
              <li><strong>Probation Officers:</strong> Only if you have assigned them access to your account</li>
              <li><strong>AI Service Providers:</strong> Lovable AI for resume generation (data minimisation applied)</li>
              <li><strong>Cloud Infrastructure:</strong> Lovable Cloud for secure data storage (UK/EU data centres)</li>
              <li><strong>Payment Processors:</strong> PCI-compliant payment processing</li>
              <li><strong>Legal Obligations:</strong> Law enforcement or regulators when legally required</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">International Transfers</h2>
            <p>
              Where personal data is transferred outside the UK we use appropriate safeguards such as adequacy decisions, standard contractual clauses, or other lawful transfer mechanisms. We document transfers and the safeguards applied. We prioritise UK/EU data centres for sensitive data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Data Security</h2>
            <p>
              We maintain organisational, technical, and physical security measures to protect personal data against unauthorised access, accidental loss, destruction, or damage.
            </p>
            <ul className="list-disc ml-6 mt-2">
              <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
              <li>Field-level encryption for sensitive data</li>
              <li>Access controls and role-based permissions</li>
              <li>Multi-factor authentication for sensitive accounts</li>
              <li>Key management with rotation</li>
              <li>Secure development practices and regular security testing</li>
              <li>Audit logging for all criminal record access (GDPR Article 30)</li>
            </ul>
            <p className="mt-2">
              Access to production personal data is restricted, logged, and reviewed.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Retention and Deletion</h2>
            <p>
              We retain personal data only as long as necessary for the stated purposes or as required by law. We apply automated deletion or anonymisation and support legal holds where necessary.
            </p>
            <ul className="list-disc ml-6 mt-2">
              <li><strong>User Profiles:</strong> 7 years from account closure (employment law requirement)</li>
              <li><strong>Criminal Records:</strong> 7 years from last disclosure or account closure (DPA 2018)</li>
              <li><strong>Consent Records:</strong> 7 years (GDPR accountability requirement)</li>
              <li><strong>Job Applications:</strong> 7 years (recruitment record retention)</li>
              <li><strong>Analytics Data:</strong> 180 days (medium retention)</li>
              <li><strong>Ephemeral Logs:</strong> 24 hours</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Subject Access and Other Rights</h2>
            <p>You have rights under UK data protection law, including:</p>
            <ul className="list-disc ml-6">
              <li><strong>Right of Access (Article 15):</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification (Article 16):</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Right to Erasure (Article 17):</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Right to Restriction (Article 18):</strong> Limit how we use your data in certain circumstances</li>
              <li><strong>Right to Data Portability (Article 20):</strong> Receive your data in a machine-readable format</li>
              <li><strong>Right to Object (Article 21):</strong> Object to processing based on legitimate interests or direct marketing</li>
              <li><strong>Right to Withdraw Consent (Article 7(3)):</strong> Withdraw consent at any time (does not affect prior processing)</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact us at{" "}
              <a href="mailto:admin@sccapp.co.uk" className="text-primary underline">
                admin@sccapp.co.uk
              </a>
              {" "}or use our{" "}
              <a href="/subject-rights" className="text-primary underline">Subject Rights Request Portal</a>.
              We will respond within 1 month (ICO guidance).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Identity Verification for Requests</h2>
            <p>
              To protect privacy we verify the identity of individuals making requests proportionately. Verification may require a combination of account credentials and a government-issued ID or other evidence for high-risk requests such as erasure of account data, unless such verification would be disproportionate.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Breach Notification</h2>
            <p>
              We maintain an incident response plan and will notify the Information Commissioner's Office (ICO) and affected individuals when a personal data breach creates a risk to people's rights and freedoms, in line with legal timeframes and guidance. Notifications will contain the nature of the breach, affected categories, likely consequences, and mitigation steps taken.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Children's Data</h2>
            <p>
              We do not knowingly collect personal data from children under 13 without parental consent. Where our services are directed at young people we obtain verifiable parental consent where required and provide clear, age-appropriate privacy information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Marketing and Communications</h2>
            <p>
              We only send marketing communications where we have your consent or another lawful basis. You may opt out of marketing at any time via the unsubscribe link in emails or via your account settings. Opting out does not affect other service communications.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Automated Decision Making and Profiling</h2>
            <p>
              We do not rely on automated decision making that has legal or similarly significant effects on users unless explicitly disclosed and lawful bases are met. Where profiling is used for personalisation (such as AI-powered job matching), users will be given meaningful information and, where required, the right to object.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Links to Other Sites</h2>
            <p>
              Our site may contain links to other websites. We are not responsible for the privacy practices of third parties. We encourage you to read their privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Changes to This Policy</h2>
            <p>
              We may update this policy to reflect changes in legal or operational requirements. Where changes are significant we will provide prominent notice via email or platform notification and obtain consents again if required. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Contact and Complaints</h2>
            <p>
              For questions, requests, or complaints about this policy or our processing, contact:{" "}
              <a href="mailto:admin@sccapp.co.uk" className="text-primary underline">
                admin@sccapp.co.uk
              </a>
            </p>
            <p className="mt-4">
              You also have the right to lodge a complaint with the Information Commissioner's Office (ICO):
            </p>
            <ul className="list-disc ml-6">
              <li>Website: <a href="https://ico.org.uk" className="text-primary underline" target="_blank" rel="noopener noreferrer">https://ico.org.uk</a></li>
              <li>Phone: 0303 123 1113</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Practical Summary for Users</h2>
            <ul className="list-disc ml-6">
              <li>You control the data you share; you can access, correct, delete, and restrict processing</li>
              <li>We use strong technical and organisational measures to protect your data and limit sharing to necessary processors under contract</li>
              <li>We retain data only for as long as required and document lawful bases for processing</li>
              <li>Criminal conviction data is processed only with explicit consent and under strict safeguards</li>
            </ul>
          </section>

          <div className="mt-12 p-6 bg-muted rounded-lg">
            <p className="text-sm">
              <strong>Effective Date:</strong> 16 October 2025
            </p>
            <p className="text-sm mt-2">
              <strong>Privacy by Design:</strong> This platform was built with privacy-first principles, implementing GDPR Article 25 (data protection by design and by default) and DPA 2018 requirements for handling criminal conviction data responsibly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
